import { z } from 'zod';
import { STAGES } from '../constants/leadContato';
import { validateCPF } from '../utils/cpfValidator';
import { validatePixKey, validateCNPJ } from "../utils/pixValidator";

export const clinicaSchema = z.object({
    razaoSocial: z.string().min(1, "Razão Social é obrigatória"),
    cnpj: z.string().min(1, "CNPJ é obrigatório").refine((val) => validateCNPJ(val), {
        message: "CNPJ inválido",
    }),
    isValidCNPJ: z.literal(true, { errorMap: () => ({ message: "O CNPJ deve estar com a situação cadastral Ativa." }) }),
    cnes: z.string().optional().or(z.literal("")),
    nomeFantasia: z.string().optional().or(z.literal("")),
    telefone: z.string().optional().or(z.literal("")),
    celularWhatsApp: z.string().optional().or(z.literal("")),
    cep: z.string().min(8, "CEP inválido"),
    endereco: z.string().min(1, "Endereço é obrigatório"),
    numero: z.string().min(1, "Número é obrigatório"),
    complemento: z.string().optional().or(z.literal("")),
    bairro: z.string().min(1, "Bairro é obrigatório"),
    cidade: z.string().min(1, "Cidade é obrigatória"),
    estado: z.string().min(1, "Estado é obrigatório"),
    pais: z.string().min(1, "País é obrigatório"),
    clinica: z.boolean().default(true),
    qtdSalas: z.coerce.number().min(1, "O número de salas deve ser maior que zero").int("O número de salas deve ser um número inteiro"),
    foto: z.any().optional(),
});

export const clinicaEditSchema = clinicaSchema.extend({
    qtdSalas: z.coerce.number().int().min(1).optional().or(z.literal("")),
});

export const leadSchema = z.object({
    nomeCompleto: z.string().min(1, 'Nome é obrigatório'),
    queixaSintoma: z.string().min(1, 'Queixa/Sintoma é obrigatório'),
    especialidade: z.string().min(1, 'Especialidade é obrigatória'),
    email: z.string().optional(),
    telefone: z.string().refine(val => {
        const digits = val.replace(/\D/g, '').length;
        return digits === 10 || digits === 11;
    }, {
        message: 'Telefone deve ter 10 (fixo) ou 11 (celular) dígitos com DDD'
    }),
    ondeNosConheceu: z.string().min(1, 'Selecione uma opção'),
    indicadoPorPaciente: z.string().optional(),
    outraIndicacao: z.string().optional()
}).superRefine((data, ctx) => {
    if (data.ondeNosConheceu === 'indicacao' && !data.indicadoPorPaciente) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Selecione o paciente que indicou',
            path: ['indicadoPorPaciente']
        });
    }
    if ((data.ondeNosConheceu === 'outro' || data.ondeNosConheceu === 'parceria') && !data.outraIndicacao) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Este campo é obrigatório',
            path: ['outraIndicacao']
        });
    }
});

export const contatoSchema = z.object({
    // Permitimos string opcional ou vazia no esquema base
    proximoContato: z.string().optional().or(z.literal('')),
    prospeccao: z.boolean().default(false),
    abordagem: z.boolean().default(false),
    apresentacao: z.boolean().default(false),
    followUp: z.boolean().default(false),
    negociacao: z.boolean().default(false),
    fechamento: z.boolean().default(false),
    comentarios: z.string().optional()
}).superRefine((data, ctx) => {
    // 1. Validação de obrigatoriedade condicional da data
    // Se NÃO for fechamento e a data estiver vazia, gera erro
    if (!data.fechamento && (!data.proximoContato || data.proximoContato.trim() === '')) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'A data do próximo contato é obrigatória quando não há fechamento',
            path: ['proximoContato']
        });
    }

    // 2. Verifica se pelo menos uma etapa do funil foi selecionada
    const hasStage = STAGES.some(stage => data[stage.value]);

    if (!hasStage) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Selecione pelo menos uma etapa do contato',
            path: ['prospeccao']
        });
    }
});

export const profissionalSaudeSchema = z.object({
    nomeCompleto: z.string().min(3, "Nome muito curto"),
    cpf: z.string().min(1, "CPF é obrigatório").refine(validateCPF, "CPF inválido"),
    sexo: z.string().min(1, "Sexo é obrigatório"),
    email: z.string().email("E-mail inválido"),
    clinica: z.string().optional(),
    especialidades: z.array(z.string()).min(1, "Selecione ao menos uma especialidade"),
    nomeBanco: z.string().min(1, "Nome do banco é obrigatório"),
    titular: z.string().min(1, "Titular é obrigatório"),
    banco: z.union([
        z.object({
            value: z.union([z.string(), z.number()]),
            label: z.string()
        }),
        z.string(),
        z.number()
    ], { required_error: "Selecione um banco na lista" }).nullable(),
    agencia: z.string().min(1, "Agência é obrigatória"),
    digitoAgencia: z.string().optional(),
    numeroConta: z.string().min(1, "Número da conta é obrigatório"),
    digitoConta: z.string().optional(),
    foto: z.any().optional(),
    fotoId: z.any().optional(),
}).passthrough();

export const schemaColaborador = z.object({
    nomeCompleto: z.string().min(2, "Nome deve ter ao menos 2 caracteres"),
    cpf: z.string().min(1, "CPF é obrigatório").refine((val) => validateCPF(val), {
        message: "CPF inválido",
    }),
    sexo: z.string().min(1, "Sexo é obrigatório"),
    email: z.string().email("E-mail inválido"),
    telefone: z.string().optional().refine((val) => !val || val.replace(/\D/g, '').length === 10, {
        message: "Fixo deve ter 10 dígitos",
    }),
    celularWhatsApp: z.string().refine((val) => val.replace(/\D/g, '').length === 11, {
        message: "Celular é obrigatório (11 dígitos)",
    }),
    funcao: z.string().min(1, "Função é obrigatória"),
    profissao: z.string().min(1, "Profissão é obrigatória"),
    cep: z.string().min(8, "CEP é obrigatório"),
    endereco: z.string().min(1, "Endereço é obrigatório"),
    numero: z.string().min(1, "Número é obrigatório"),
    complemento: z.string().optional(),
    bairro: z.string().min(1, "Bairro é obrigatório"),
    cidade: z.string().min(1, "Cidade é obrigatória"),
    estado: z.string().min(1, "Estado é obrigatório"),
    pais: z.string().min(1, "País é obrigatório"),
    ativa: z.boolean().optional(),
    ehContaCaixa: z.boolean().optional(),
    nomeBanco: z.string().optional(),
    titular: z.string().min(1, "Titular é obrigatório"),
    banco: z.any().optional(),
    agencia: z.string().optional(),
    digitoAgencia: z.string().optional(),
    numeroConta: z.string().optional(),
    digitoConta: z.string().optional(),
    tipoPix: z.string().optional(),
    numeroPix: z.string().optional(),
    foto: z.any().optional(),
    fotoId: z.any().optional(),
    clinica: z.any().optional(),
}).superRefine((data, ctx) => {
    if (data.numeroPix && data.tipoPix) {
        const { valid, message } = validatePixKey(data.tipoPix, data.numeroPix);
        if (!valid) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: message,
                path: ["numeroPix"],
            });
        }
    }
});


export const schemaEspecialidade = z.object({
    titulo: z.string().min(1, "Título é obrigatório"),
    valor: z.number().optional(),
    duracao: z.string()
        .regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Informe uma duração válida")
        .refine((value) => {
            const [horas, minutos] = value.split(":").map(Number);
            return horas * 60 + minutos > 0;
        }, "A duração deve ser maior que zero"),
    emGrupo: z.boolean().optional(),
    atendimentosPorHora: z.string().optional(),
    cor: z.string().min(1, "Cor é obrigatória"),
    clinica: z.string().optional(),
}).superRefine((data, ctx) => {
    if (data.emGrupo && !data.atendimentosPorHora) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Campo obrigatório",
            path: ["atendimentosPorHora"],
        });
    }
});


export const convenioSchema = z.object({
    nomeConvenio: z.string().min(1, "Nome do convênio é obrigatório"),
    tipoConvenio: z.string().min(1, "Tipo do convênio é obrigatório"),
    registroANS: z.string().min(1, "Registro ANS é obrigatório"),
    cnpj: z.string().optional().or(z.literal("")),
    isValidCNPJ: z.boolean().optional(),
    cnes: z.string().optional().or(z.literal("")),
    email: z.string().min(1, "E-mail é obrigatório").email("E-mail inválido"),
    telefone: z.string().optional().or(z.literal("")),
    celular: z.string().optional().or(z.literal("")),
    celularWhatsApp: z.string().optional().or(z.literal("")),
    responsavelCadastro: z.string().min(1, "Responsável do cadastro é obrigatório"),
    tabelaProcedimento: z.array(z.string()).min(1, "Selecione pelo menos uma tabela de procedimento"),
    periodoRetorno: z.coerce.number({ invalid_type_error: "Período de retorno é obrigatório" })
        .min(1, "Deve ser maior que 0"),
    tempoParaReceber: z.string().optional().or(z.literal(""))
        .refine((val) => !val || Number(val) > 0, { message: "Deve ser maior que 0" }),
    diaRecebimento: z.string().optional().or(z.literal(""))
        .refine((val) => !val || Number(val) > 0, { message: "Deve ser maior que 0" }),
    cep: z.string().min(8, "CEP é obrigatório"),
    endereco: z.string().optional().or(z.literal("")),
    numero: z.string().optional().or(z.literal("")),
    complemento: z.string().optional().or(z.literal("")),
    bairro: z.string().optional().or(z.literal("")),
    cidade: z.string().optional().or(z.literal("")),
    estado: z.string().optional().or(z.literal("")),
    pais: z.string().optional().or(z.literal("")),
    clinica: z.string().optional().or(z.literal("")),
}).superRefine((data, ctx) => {
    const digits = data.cnpj?.replace(/\D/g, '') ?? '';
    if (digits.length === 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'CNPJ é obrigatório.', path: ['cnpj'] });
    } else if (!validateCNPJ(data.cnpj)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'CNPJ inválido.', path: ['cnpj'] });
    } else if (!data.isValidCNPJ) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'O CNPJ deve estar com a situação cadastral Ativa.', path: ['isValidCNPJ'] });
    }
});

export const schemaFornecedor = z.object({
    razaoSocial: z.string().min(1, 'Razão social é obrigatória.'),
    nomeContato: z.string().trim().optional(),
    email: z.string().optional()
        .transform((value) => (value ?? '').split(';').map((email) => email.trim()).filter(Boolean).join('; '))
        .refine((value) => !value || value.split('; ').every((email) => z.string().email().safeParse(email).success), {
            message: 'Informe e-mails válidos separados por ponto e vírgula (;).',
        }),
    cnpj: z.string().optional(),
    isValidCNPJ: z.boolean().optional(),
    cnes: z.string().optional(),
    nomeFantasia: z.string().optional(),
    telefone: z.string().optional(),
    celularWhatsApp: z.string().optional(),
    cep: z.string().optional(),
    endereco: z.string().optional(),
    numero: z.string().optional(),
    complemento: z.string().optional(),
    bairro: z.string().optional(),
    cidade: z.string().optional(),
    estado: z.string().optional(),
    pais: z.string().optional(),
    foto: z.any().optional(),
    fotoId: z.any().optional(),
    clinica: z.any().optional(),
}).superRefine((data, ctx) => {
    const digits = data.cnpj?.replace(/\D/g, '') ?? '';
    if (digits.length > 0) {
        if (!validateCNPJ(data.cnpj)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'CNPJ inválido.', path: ['cnpj'] });
        } else if (data.isValidCNPJ === false) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'O CNPJ deve estar com a situação cadastral Ativa.', path: ['isValidCNPJ'] });
        }
    }
});

export const schemaProcedimento = z.object({
    titulo: z.string().min(1, "Título é obrigatório"),
    valor: z
        .number({ invalid_type_error: "Valor é obrigatório" })
        .min(1, "Informe o valor do procedimento"),
    duracao: z.string().min(1, "Duração é obrigatória"),
    cor: z.string().min(1, "Cor é obrigatória"),
    clinica: z.string().optional(),
    particular: z.boolean().default(false),
    convenio: z.string().nullable().optional(),
});

export const schemaPlano = z.object({
    titulo: z.string().min(1, "Título é obrigatório"),
    duracaoTempoAula: z.string().min(1, "Campo obrigatório"),
    cor: z.string().min(1, "Cor é obrigatória"),
    numeroAulas: z.coerce.number().min(1, "Número de sessões obrigatório"),
    vezesPorSemana: z.coerce.number().min(1, "Número de vezes por semana obrigatório"),
    duracaoMeses: z.coerce.number().min(1, "Duração do plano em meses obrigatório"),
    valorTotal: z.number().min(1, "Valor total obrigatório"),
    valorUnitario: z.number().optional(),
    valorMensalidade: z.number().optional(),
    atendimentosPorHora: z.coerce.number().optional(),
    clinica: z.string().optional(),
    especialidade: z.string().min(1, "Selecione uma especialidade"),
});

// Cadastro completo de Produto (screens/Financas/ProdutoFormScreen) — espelha no frontend as
// mesmas validações do backend (produtoService.validarValores / model Produto) pra dar feedback
// antes do submit. Campos condicionais de `dadosEspecificos` não entram aqui (variam por
// categoria e não têm regra de obrigatoriedade própria).
export const schemaProdutoCompleto = z.object({
    codInterno: z.string().optional(),
    codBarras: z.string().optional(),
    nomeProduto: z.string().min(1, "Nome do produto é obrigatório"),
    nomeGenerico: z.string().optional(),
    categoria: z.string().min(1, "Selecione a categoria"),
    subcategoria: z.string().optional(),
    fabricante: z.string().min(1, "Fabricante é obrigatório"),
    unidadeMedida: z.string().min(1, "Unidade de medida é obrigatória"),
    qtdEmbalagens: z.coerce.number().nonnegative("Não pode ser negativo").optional(),
    itensPorEmbalagem: z.coerce.number().nonnegative("Não pode ser negativo").optional(),
    casasDecimaisQuantidade: z.coerce.number().min(0).max(3).optional(),
    tipoUso: z.string().optional(),
    habilitadoParaProcedimento: z.boolean().optional(),
    dadosEspecificos: z.record(z.any()).optional(),
    observacoesArmazenamento: z.string().optional(),
    controladoLote: z.boolean().optional(),
    perecivel: z.boolean().optional(),
    controladoPortaria: z.boolean().optional(),
    portaria: z.object({
        listaControle: z.string().optional(),
        numero: z.string().optional(),
        orgaoEmissor: z.string().optional(),
        data: z.string().optional(),
        requerNotificacaoEspecial: z.boolean().optional(),
        observacoes: z.string().optional(),
    }).optional(),
    estoqueMinimoPadrao: z.coerce.number().nonnegative("Não pode ser negativo").optional(),
    estoqueMaximo: z.coerce.number().nonnegative("Não pode ser negativo").optional(),
    pontoReposicao: z.coerce.number().nonnegative("Não pode ser negativo").optional(),
    localizacao: z.string().optional(),
    fornecedorPrincipal: z.string().min(1, "Selecione o fornecedor principal"),
    prazoEntregaDias: z.coerce.number().nonnegative("Não pode ser negativo").optional(),
    fornecedoresAlternativos: z.array(z.string()).optional(),
    precoCompraEmbalagem: z.coerce.number().nonnegative("Não pode ser negativo"),
    precoVendaFinal: z.coerce.number().nonnegative("Não pode ser negativo").optional(),
    ncm: z.string().optional(),
    // Dados fiscais do XML (entrada) e da operação de saída — estrutura validada no backend.
    fiscal: z.record(z.any()).optional(),
    markup: z.object({
        despesasFixasPerc: z.coerce.number().nonnegative().optional(),
        despesasVariaveisPerc: z.coerce.number().nonnegative().optional(),
        lucroLiquidoPerc: z.coerce.number().nonnegative().optional(),
    }).optional(),
    status: z.string().optional(),
    observacoes: z.string().optional(),
    clinica: z.string().optional(),
});

export const schemaProdutoEntrada = z.object({
    tipoEntrada: z.string().optional(),
    responsavel: z.string().optional(),
    fornecedor: z.string().transform(v => v || undefined).optional(),
    numeroDocumento: z.string().optional(),
    chaveAcesso: z.string().regex(/^(\d{44})?$/, "Informe 44 dígitos").optional(),
    serieDocumento: z.string().optional(),
    dataVencimento: z.string().transform(v => v || undefined).optional(),
    dataDocumento: z.string().optional(),
    valorTotalNF: z.number().nonnegative().optional(),
    transportadora: z.string().optional(),
    frete: z.string().optional(),
    valorFrete: z.number().nonnegative().optional(),
    qtdVolumes: z.coerce.number().nonnegative().optional(),
    obsFornecedor: z.string().optional(),
    numeroLote: z.string().optional(),
    dataFabricacao: z.string().optional(),
    dataValidade: z.string().optional(),
    requerQualidade: z.boolean().optional(),
    resultadoQualidade: z.string().optional(),
    obsQualidade: z.string().optional(),
    recebidoConferido: z.boolean().optional(),
    // Dados fiscais do item no XML (importação) — o backend sanea; sem isso o Zod descartaria o campo.
    fiscal: z.record(z.any()).optional(),
    regimeAplicado: z.string().optional(),

    produtoId: z.string().min(1, "Selecione um produto"),
    nomeProduto: z.string().min(1, "Selecione um produto"),
    dataEntrada: z.string().min(1, "Data de entrada é obrigatória"),
    horario: z.string().min(1, "Horário é obrigatório"),
    pacoteUnidade: z.string().min(1, "Selecione o tipo"),
    quantidadePorPacote: z.coerce.number().min(1, "Informe a quantidade por pacote"),
    quantidade: z.coerce.number().min(1, "Informe a quantidade"),
    valorPago: z.number().nonnegative("O valor pago não pode ser negativo").optional(),
    valor: z.number().optional(),
    tipoControle: z.string().min(1, "Selecione o tipo de controle"),
    unidadeMedida: z.string().min(1, "Selecione a unidade de medida"),
    codigoBarras: z.string().optional(),
    observacao: z.string().optional(),
    ativoEmpresa: z.boolean().optional(),
    controladoLote: z.boolean().optional(),
    fracionarProduto: z.boolean().optional(),
    produtoFracionado: z.string().optional(),
    clinica: z.string().optional(),
}).superRefine((data, ctx) => {
    if (data.numeroDocumento?.trim() && !data.fornecedor && !data.chaveAcesso?.trim()) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["fornecedor"], message: "Informe o fornecedor para identificar a nota fiscal" });
    }
    if (data.chaveAcesso?.trim() && !/^\d{44}$/.test(data.chaveAcesso.replace(/\s/g, ""))) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["chaveAcesso"], message: "A chave de acesso deve conter 44 dígitos" });
    }
    if (data.controladoLote && !data.numeroLote?.trim()) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["numeroLote"], message: "Informe o número do lote" });
    }
    if (data.dataFabricacao && data.dataValidade && data.dataFabricacao > data.dataValidade) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["dataValidade"], message: "A validade deve ser igual ou posterior à fabricação" });
    }
});

export const schemaProdutoSaida = z.object({
    produtoId: z.string().min(1, "Selecione um produto"),
    nomeProduto: z.string().min(1, "Selecione um produto"),
    dataSaida: z.string().min(1, "Data de saída é obrigatória"),
    horario: z.string().min(1, "Horário é obrigatório"),
    pacoteUnidade: z.string().min(1, "Selecione o tipo"),
    quantidadePorPacote: z.coerce.number().min(1, "Informe a quantidade por pacote"),
    quantidade: z.coerce.number().min(1, "Informe a quantidade"),
    valor: z.number().optional(),
    tipoControle: z.string().min(1, "Selecione o tipo de controle"),
    unidadeMedida: z.string().min(1, "Selecione a unidade de medida"),
    codigoBarras: z.string().optional(),
    observacao: z.string().optional(),
    ativoEmpresa: z.boolean().optional(),
    controladoLote: z.boolean().optional(),
    fracionarProduto: z.boolean().optional(),
    produtoFracionado: z.string().optional(),
    responsavel: z.string().optional(),
    destino: z.string().optional(),
    solicitante: z.string().optional(),
    numeroLote: z.string().optional(),
    dataValidade: z.string().optional(),
    requerQualidade: z.boolean().optional(),
    resultadoQualidade: z.string().optional(),
    obsQualidade: z.string().optional(),
    autorizadaFarmaceutico: z.boolean().optional(),
    farmaceutico: z.string().optional(),
    crf: z.string().optional(),
    requerReceita: z.boolean().optional(),
    tipoReceita: z.string().optional(),
    numeroReceita: z.string().optional(),
    dataReceita: z.string().optional(),
    prescritor: z.string().optional(),
    crm: z.string().optional(),
    precoVenda: z.number().min(0).optional(),
    desconto: z.number().min(0).optional(),
    formaPagamento: z.string().optional(),
    obsFinanceira: z.string().optional(),
    clinica: z.string().optional(),
}).superRefine((data, ctx) => {
    const required = (name, message) => {
        if (!data[name]?.trim()) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [name], message });
    };
    if (data.controladoLote) required("numeroLote", "Informe o número do lote");
    if (data.requerQualidade) required("resultadoQualidade", "Informe o resultado da conferência");
    if (data.autorizadaFarmaceutico) {
        required("farmaceutico", "Informe o farmacêutico");
        required("crf", "Informe o CRF");
    }
    if (data.requerReceita) {
        required("tipoReceita", "Informe o tipo de receita");
        required("numeroReceita", "Informe o número da receita");
    }
    if (data.tipoControle === "venda" && (data.desconto || 0) > (data.precoVenda || 0) * data.quantidade * data.quantidadePorPacote) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["desconto"], message: "O desconto não pode exceder o valor da venda" });
    }
});

export const schemaPacote = z.object({
    titulo: z.string().min(1, "Título é obrigatório"),
    duracaoTempoSessao: z.string().min(1, "Campo obrigatório"),
    cor: z.string().min(1, "Cor é obrigatória"),
    numeroSessoes: z.coerce.number().min(1, "Número de sessões obrigatório"),
    duracaoMeses: z.coerce.number().min(1, "Duração do plano em meses obrigatório"),
    valorTotal: z.number().min(1, "Valor total obrigatório"),
    valorUnitario: z.number().optional(),
    valorMensalidade: z.number().optional(),
    atendimentosPorHora: z.coerce.number().optional(),
    clinica: z.string().optional(),
    especialidade: z.string().min(1, "Selecione uma especialidade"),
});

export const schemaTransferirEstoque = z.object({
    toClinicId: z.string().min(1, "Selecione a clínica de destino"),
    quantity: z.coerce.number().min(1, "Informe a quantidade a transferir"),
});

const descontoFields = {
    descontoReal: z.number().nullable().optional(),
    descontoPorcentagem: z.number().nullable().optional(),
};

const refineDesconto = (data, ctx) => {
    if (data.descontoReal != null && data.descontoPorcentagem != null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Informe desconto em valor OU em porcentagem, não os dois.",
            path: ["descontoPorcentagem"],
        });
    }
};

export const schemaPacienteCompraProduto = z.object({
    numeroLote: z.string().optional(),
    produtoId: z.string().min(1, "Selecione um produto"),
    quantidade: z.coerce.number().min(1, "Informe a quantidade"),
    valorUnitario: z.number().min(1, "Informe o valor unitário"),
    formaPagamentoId: z.string().min(1, "Selecione a forma de pagamento"),
    modalidadeCartao: z.string().optional(),
    pagamentoCartaoParcelas: z.number().optional(),
    pagamentoCartaoAutorizacao: z.string().optional(),
    ...descontoFields,
    observacoes: z.string().optional(),
}).superRefine(refineDesconto);

export const schemaPacienteCompraConsultaAvulsa = z.object({
    especialidade: z.string().min(1, "Selecione a especialidade"),
    profissionalId: z.string().optional(),
    valor: z.number().min(1, "Informe o valor"),
    formaPagamentoId: z.string().min(1, "Selecione a forma de pagamento"),
    modalidadeCartao: z.string().optional(),
    pagamentoCartaoParcelas: z.number().optional(),
    pagamentoCartaoAutorizacao: z.string().optional(),
    ...descontoFields,
    observacoes: z.string().optional(),
}).superRefine(refineDesconto);

export const schemaPacienteCompraProcedimento = z.object({
    procedimentoId: z.string().min(1, "Selecione o procedimento"),
    valor: z.number().min(1, "Informe o valor"),
    formaPagamentoId: z.string().min(1, "Selecione a forma de pagamento"),
    modalidadeCartao: z.string().optional(),
    pagamentoCartaoParcelas: z.number().optional(),
    pagamentoCartaoAutorizacao: z.string().optional(),
    ...descontoFields,
    observacoes: z.string().optional(),
}).superRefine(refineDesconto);

export const schemaPacienteCompraPacoteOuPlano = z.object({
    procedimentos: z.array(z.object({
        procedimentoId: z.string().min(1), quantidade: z.number().int().min(1), valor: z.number().min(0),
    })).optional(),
    incluirCobrançaProcedimento: z.boolean().optional(),
    especialidade: z.string().min(1, "Selecione a especialidade"),
    origemTipo: z.enum(["pacote", "plano"]),
    origem: z.string().min(1, "Selecione o pacote/plano"),
    formaPagamentoId: z.string().min(1, "Selecione a forma de pagamento"),
    modalidadeCartao: z.string().optional(),
    pagamentoCartaoParcelas: z.number().optional(),
    pagamentoCartaoAutorizacao: z.string().optional(),
    ...descontoFields,
    observacoes: z.string().optional(),
}).superRefine(refineDesconto);

// Estorno de plano/pacote contratado — mesmos enums do backend (models/Financas/Estorno/Estorno.js).
export const schemaEstorno = z.object({
    motivo: z.enum(
        ["desistencia_arrependimento", "insatisfacao", "problema_saude", "falta_agenda", "outros"],
        { errorMap: () => ({ message: "Selecione o motivo do estorno" }) },
    ),
    isencaoMulta: z.enum(["nenhuma", "quebra_contrato_clinica", "forca_maior"]).default("nenhuma"),
    percentualMulta: z.number().min(0).max(100).optional(),
    outrasTaxas: z.array(z.object({
        descricao: z.string().min(1, "Descrição obrigatória"),
        valor: z.number().min(1, "Valor deve ser maior que zero"),
    })).optional(),
    observacoes: z.string().optional(),
    formaPagamentoOriginal: z.enum(
        ["cartao_credito", "pix", "boleto", "transferencia"],
        { errorMap: () => ({ message: "Selecione a forma de pagamento original" }) },
    ),
    nomeTitular: z.string().min(1, "Nome do titular é obrigatório"),
    cpfTitular: z.string().min(1, "CPF do titular é obrigatório").refine(validateCPF, "CPF inválido"),
    banco: z.string().optional(),
    agencia: z.string().optional(),
    conta: z.string().optional(),
    chavePix: z.string().optional(),
}).superRefine((data, ctx) => {
    if (data.formaPagamentoOriginal === "pix" && !data.chavePix) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Chave Pix é obrigatória", path: ["chavePix"] });
    }
});

// Nova versão da ficha técnica de materiais padrão de um procedimento.
export const schemaProcedimentoMaterialPadrao = z.object({
    vigenciaInicio: z.string().min(1, "Informe a data de vigência"),
    observacoes: z.string().optional(),
    itens: z.array(z.object({
        produto: z.string().min(1, "Selecione o produto"),
        quantidade: z.coerce.number().positive("Quantidade deve ser positiva"),
    })).min(1, "Adicione ao menos um material"),
});
